import { useState, useCallback, useEffect, useRef } from 'react';
import type { Signal } from '~/lib/api';
import { useWebSocketContext } from '~/contexts/WebSocketContext';
import type { WSEvent } from './useWebSocket';

interface UseSignalStreamOptions {
  onSignal?: (signal: Signal) => void;
  enabled?: boolean;
}

export function useSignalStream(options: UseSignalStreamOptions = {}) {
  const { onSignal, enabled = true } = options;
  const [signals, setSignals] = useState<Signal[]>([]);
  const onSignalRef = useRef(onSignal);
  onSignalRef.current = onSignal;

  const { connected, error, reconnecting, subscribe } = useWebSocketContext();

  const handleEvent = useCallback((event: WSEvent) => {
    // Handle dismissed signals (e.g., from message edits)
    if (event.type === 'signal_dismissed') {
      const { id } = event.data as { id: string };
      setSignals((prev) => prev.filter((s) => s.id !== id));
      return;
    }

    if (event.type !== 'signal') return;

    const data = event.data as unknown as Signal;

    setSignals((prev) => {
      // Replace existing signal with same id (edit replacement) or add new
      const existingIdx = prev.findIndex((s) => s.id === data.id);
      if (existingIdx >= 0) {
        const updated = [...prev];
        updated[existingIdx] = data;
        return updated;
      }
      return [data, ...prev];
    });

    onSignalRef.current?.(data);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    return subscribe(handleEvent);
  }, [enabled, subscribe, handleEvent]);

  const clearSignals = useCallback(() => {
    setSignals([]);
  }, []);

  return {
    connected,
    error,
    reconnecting,
    signals,
    clearSignals,
  };
}

/**
 * Request notification permission
 */
export async function requestNotificationPermission(): Promise<boolean> {
  if (!('Notification' in window)) {
    return false;
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  return false;
}
